import { BayLevelEnum, IBayLevelData } from "open-vessel-definition";
import { IBayPattern, ISlotPattern } from "./types/IPositionPatterns";
import { createDictionary, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";

import { IBayBlocksAndSizes } from "./types/IBayBlocksAndSizes";
import { IGetBayBlocksOutput } from "./types/IGetBayBlocksOutput";
import { IGetPairedBaysOutput } from "./getAllThePairedBays";
import TContainerLengths from "./TContainerLengths";
import { getRowsAndTiersFromSlotKeys } from "./getRowsAndTiersFromSlotKeys";
import { getSizesFromSlots } from "./getSizesFromSlots";

export function getBayBlocks(
  bls: IBayLevelData[],
  pairedBaysCalc: IGetPairedBaysOutput,
): IGetBayBlocksOutput {
  const blsDict: Record<string, IBayLevelData> = createDictionary(
    bls,
    (b) => `${b.isoBay}-${b.level}`,
  );

  const allIsoBays = bls
    .map((b) => b.isoBay)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort(sortNumericAsc)
    .map((b) => ({ isoBay: b }));

  const blockBaysAndSizesKeys = calcBlockBaysAndSizesKeys(pairedBaysCalc);

  const blockBaysAndSizes: IBayBlocksAndSizes[] = Array.from(
    blockBaysAndSizesKeys,
  )
    .sort()
    .map((b) => ({
      allBays: b,
      base: b.split("-")[0] as IBayPattern,
      blockSize: 20,
      maxSizes: {},
      numberOfBays: 1,
      sizesFromSlotsSameBay: [],
      minRow: 0,
      maxRow: 0,
      definedSlots: 0,
    }));

  bls.forEach((bl) => {
    const bay = bl.isoBay;
    const paired =
      pairedBaysCalc.pairedBaysPer20Bay[`${bl.isoBay}-${bl.level}`];

    // const paired = pairedBaysCalc.pairedBays.find(
    //   (pb) => pb.base === bay,
    // )?.paired;

    const blSameAbove = blsDict[`${bay}-${BayLevelEnum.ABOVE}`];
    const blSameBelow = blsDict[`${bay}-${BayLevelEnum.BELOW}`];

    const blPairedAbove = paired
      ? blsDict[`${paired}-${BayLevelEnum.ABOVE}`]
      : undefined;
    const blPairedBelow = paired
      ? blsDict[`${paired}-${BayLevelEnum.BELOW}`]
      : undefined;

    const sizesFromSlotsSameAbove = getSizesFromSlots(blSameAbove?.perSlotInfo),
      sizesFromSlotsSameBelow = getSizesFromSlots(blSameBelow?.perSlotInfo),
      sizesFromSlotsPairedAbove = getSizesFromSlots(blPairedAbove?.perSlotInfo),
      sizesFromSlotsPairedBelow = getSizesFromSlots(blPairedBelow?.perSlotInfo);

    const sizesFromSlotsSameBay = (
      [
        ...(sizesFromSlotsSameAbove?.sizes || []),
        ...(sizesFromSlotsSameBelow?.sizes || []),
        20,
      ] as TContainerLengths[]
    ).filter((v, i, a) => a.indexOf(v) === i);

    const sizesFromSlotsPairedBay = (
      [
        ...(sizesFromSlotsPairedAbove?.sizes || []),
        ...(sizesFromSlotsPairedBelow?.sizes || []),
        20,
      ] as TContainerLengths[]
    ).filter((v, i, a) => a.indexOf(v) === i);

    const hasCenterLine =
      blSameAbove?.centerLineRow ||
      blSameBelow?.centerLineRow ||
      blPairedAbove?.centerLineRow ||
      blPairedBelow?.centerLineRow ||
      0;

    const maxRow = Math.max(
      calculateMaxRow(blSameAbove) || 0,
      calculateMaxRow(blSameBelow) || 0,
      calculateMaxRow(blPairedAbove) || 0,
      calculateMaxRow(blPairedBelow) || 0,
    );

    const definedSlots =
      (sizesFromSlotsSameAbove?.definedSlots || 0) +
      (sizesFromSlotsSameBelow?.definedSlots || 0) +
      (sizesFromSlotsPairedAbove?.definedSlots || 0) +
      (sizesFromSlotsPairedBelow?.definedSlots || 0);

    const maxSizeSameBay = Math.max(...sizesFromSlotsSameBay);
    const maxSizePairedBay = paired ? Math.max(...sizesFromSlotsPairedBay) : 0;

    const id = paired ? `${bay}-${paired}` : bay;
    const idx = blockBaysAndSizes.findIndex((b) => b.allBays === id);

    if (idx !== -1) {
      blockBaysAndSizes[idx].maxSizes = {
        [bay]: maxSizeSameBay,
      };

      blockBaysAndSizes[idx].minRow = hasCenterLine ? 0 : 1;
      blockBaysAndSizes[idx].maxRow = maxRow;
      blockBaysAndSizes[idx].definedSlots = definedSlots;

      blockBaysAndSizes[idx].sizesFromSlotsSameBay = sizesFromSlotsSameBay;

      if (paired) {
        blockBaysAndSizes[idx].maxSizes[paired] = maxSizePairedBay;
        blockBaysAndSizes[idx].numberOfBays = 2;
        const maxSizeOfBoth = Math.max(maxSizeSameBay, maxSizePairedBay);

        blockBaysAndSizes[idx].sizesFromSlotsPairedBay =
          sizesFromSlotsPairedBay;

        if (maxSizeOfBoth >= 40) {
          blockBaysAndSizes[idx].blockSize = maxSizeOfBoth;
        } else {
          blockBaysAndSizes[idx].blockSize = maxSizeSameBay + maxSizePairedBay;
        }
      } else {
        blockBaysAndSizes[idx].blockSize = maxSizeSameBay;
        blockBaysAndSizes[idx].numberOfBays = 1;
      }
    }
  });

  const blockBaysAndSizesBy20Bay = blockBaysAndSizes.reduce((acc, b) => {
    if (b.blockSize === 0 || b.blockSize === -Infinity) return acc;

    b.allBays.split("-").forEach((bay) => {
      acc[bay as IBayPattern] = b;
    });

    return acc;
  }, {} as Record<IBayPattern, IBayBlocksAndSizes>);

  const blockBaysAndSizesByBlockBay = createDictionary(
    blockBaysAndSizes,
    (b) => b.allBays,
  );

  return {
    allIsoBays,
    blockBaysAndSizes,
    blockBaysAndSizesByBlockBay,
    blockBaysAndSizesBy20Bay,
  };
}

function calcBlockBaysAndSizesKeys(pairedBaysCalc: IGetPairedBaysOutput) {
  const { unpairedBays, pairedBays } = pairedBaysCalc;

  const blockBaysAndSizesKeys: Map<string, number> = new Map();

  unpairedBays.forEach((ub) => {
    blockBaysAndSizesKeys.set(ub.base, ub.definedSlots);
  });

  pairedBays.forEach((pb) => {
    if (
      (!blockBaysAndSizesKeys.has(pb.base) ||
        blockBaysAndSizesKeys.get(pb.base) === 0) &&
      (!blockBaysAndSizesKeys.has(pb.paired) ||
        blockBaysAndSizesKeys.get(pb.paired) === 0)
    ) {
      blockBaysAndSizesKeys.set(pb.allBays.join("-"), 0);
    } else {
      blockBaysAndSizesKeys.set(pb.base, 0);
      blockBaysAndSizesKeys.set(pb.paired, 0);
    }
  });

  return new Set(blockBaysAndSizesKeys.keys());
}

function calculateMaxRow(bl: IBayLevelData | undefined): number | undefined {
  if (!bl) return undefined;

  const { perSlotInfo } = bl;
  const { maxRow } = getRowsAndTiersFromSlotKeys(
    Object.keys(perSlotInfo || {}) as ISlotPattern[],
  );

  return maxRow !== undefined ? Number(maxRow) : undefined;
}
